import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { useGenericFieldsData } from 'containers/GenericFieldsHandlingLayer/context';
import type { IWholeSchemaWithState } from 'containers/GenericFieldsHandlingLayer/types';
import type { BrandModule } from 'common/types/brandModule';
import Loader from 'common/components/Loader';
import ErrorMessage from 'common/components/ErrorMessage';
import useSearchQuery from 'utils/useSearchQuery';
import danflossBundle from './i18n/customization.bundle';
import formStyles from './containers/FormSwitcher/styles.module.scss';

export type DanflossBuildBrand = '@danfloss' | '@rigging';

const PROJECT_KEY = 'variables.project';
const DANFLOSS_PROJECT_ID = '27b730bc-31b6-5f80-b14f-d75f30ee5e3f';
const RIGGING_PROJECT_ID = '9e1660f4-433c-587e-aace-5f55eac407b8';

/** The brands of the standalone danfloss build: the hose builder and the rigging builder. */
export const DANFLOSS_BUILD_BRANDS: Record<DanflossBuildBrand, () => Promise<{ default: BrandModule }>> = {
  '@danfloss': () => import(/* webpackChunkName: "brand-danfloss" */ '@danfloss/brand'),
  '@rigging': () => import(/* webpackChunkName: "brand-rigging" */ '@rigging/brand'),
};

/**
 * The rigging project id opens the rigging builder. Any other id, an empty
 * one or none keeps the hose builder, as the build did before the choice.
 */
export const resolveDanflossBuildBrand = (projectId: unknown): DanflossBuildBrand => {
  const id = typeof projectId === 'string' ? projectId.trim().toLowerCase() : '';
  if (id === RIGGING_PROJECT_ID) {
    return '@rigging';
  }
  if (id !== '' && id !== DANFLOSS_PROJECT_ID) {
    console.warn(`i18n.variables.project "${projectId}" is not a brand of this build, the @danfloss frontend is used`); // eslint-disable-line no-console
  }
  return '@danfloss';
};

/**
 * The CSS variables of the hose builder bundle, scoped to the preloader: the
 * page looks as before the brand choice while no brand is loaded, and nothing
 * stays behind for the rigging builder.
 */
const PRELOADER_CSS_VARIABLES = Object.entries(danflossBundle.css).reduce((acc, [key, value]) => ({
  ...acc,
  [`--${key}`]: value,
}), {}) as React.CSSProperties;

type Status =
  | { kind: 'loading' }
  | { kind: 'ready', brand: BrandModule }
  | { kind: 'error', message: string };

const latestServerI18n = (queryClient: ReturnType<typeof useQueryClient>): Record<string, unknown> | undefined => {
  const entries = queryClient.getQueriesData<IWholeSchemaWithState>(['whole-schema']);
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    const data = entries[i][1];
    if (data && data.i18n) {
      return data.i18n;
    }
  }
  return undefined;
};

/**
 * Waits for the first schema answer, then loads the brand chosen by
 * `i18n.variables.project` and re-applies the served i18n over its texts.
 * A schema error loads the hose builder too, which shows it as before.
 */
export const DanflossBuildSwitcher = () => {
  const { i18n } = useTranslation();
  const { isLoading, error } = useGenericFieldsData();
  const queryClient = useQueryClient();
  const searchParams = useSearchQuery();
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    const projectId = i18n.getResource('en', 'translation', PROJECT_KEY);
    const settled = projectId !== undefined || !isLoading || !!error;
    if (!settled) {
      return;
    }
    startedRef.current = true;

    const key = resolveDanflossBuildBrand(projectId);
    DANFLOSS_BUILD_BRANDS[key]()
      .then(({ default: brand }) => {
        i18n.addResourceBundle('en', 'translation', brand.i18n, true, true);
        const serverI18n = latestServerI18n(queryClient);
        if (serverI18n) {
          i18n.addResourceBundle('en', 'translation', serverI18n, true, true);
        }
        setStatus({ kind: 'ready', brand });
        return i18n.changeLanguage('en');
      })
      .catch(() => {
        setStatus({ kind: 'error', message: `Failed to load the ${key} frontend, please reload the page` });
      });
  });

  if (status.kind === 'ready') {
    const View = 'printId' in searchParams ? status.brand.PrintView : status.brand.FormSwitcher;
    return <View />;
  }
  if (status.kind === 'error') {
    return <ErrorMessage text={status.message} />;
  }
  return (
    <div className={formStyles.wrapper} style={PRELOADER_CSS_VARIABLES}>
      <Loader middle className={formStyles.loading} />
    </div>
  );
};

export default DanflossBuildSwitcher;
